// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XPopup from "@gcg/App/Components/XPopup/XPopup";
import SetAllCartLinesCertificate from "@gcg/App/Handlers/Pages/Cart/SetAllCartLinesCertificate";
import CartSaveOrderButton, { SaveOrderStyles } from "@gcg/Overrides/Widgets/Cart/CartSaveOrderButton";
import AddOutline from "@gcg/XDL/Icons/AddOutline";
import Delete from "@gcg/XDL/Icons/Delete";
import Info from "@gcg/XDL/Icons/Info";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canAddAllToList,
    canSaveOrder,
    getCurrentCartState,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        cart,
        isCartEmpty: isCartEmpty(cart),
        canSaveOrder: canSaveOrder(cart),
        canAddAllToList: canAddAllToList(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        customSettings: getSettingsCollection(state).customSettings as any,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    SetAllCartLinesCertificate,
};

interface AdditionalProps {
    onRemoveAllClick: () => void;
}

type Props = WidgetProps &
    AdditionalProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartActionsStyles {
    narrowHidden?: InjectableCss;
    narrowOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderClickable?: SaveOrderStyles;
    saveOrderClickableHidden?: SaveOrderStyles;
    addAllToListClickable?: LinkPresentationProps;
    requestAQuoteClickable?: ClickablePresentationProps;
    wideHidden?: HiddenProps;
    wideOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    removeAllLink?: LinkPresentationProps;
    checkboxesWrapper?: InjectableCss;
    certificationCheckboxes?: CheckboxPresentationProps;
    certificationCheckboxesFirst?: CheckboxPresentationProps;
    hiddenOnLarge?: InjectableCss;
    buttonsOverflowPopup?: OverflowMenuPresentationProps;
}

export const cartActionsStyles: CartActionsStyles = {
    narrowHidden: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            flex-grow: 1;

            & > button {
                font-size: 12px;
                line-height: 18px;
            }
        `,
    },
    hiddenOnLarge: {
        css: css`
            display: none;
            @media (max-width: 800px) {
                display: block;
                position: absolute;
                top: 0;
                right: 0;
            }
        `,
    },
    buttonsOverflowPopup: {
        buttonProps: {
            css: css`
                padding: 0;
                height: auto;
                width: auto !important;
                border: 0;
            `,
        },
        cssOverrides: {
            wrapper: css`
                width: auto;
                height: auto;
                padding: 0;
            `,
            menu: css`
                left: auto !important;
                right: 10px;
                width: 200px;
                padding: 10px 5px;
            `,
            menuItem: css`
                display: inline;
                padding: 0;
                width: auto;
                & > * {
                }
            `,
        },
    },
    removeAllLink: {
        typographyProps: {
            css: css`
                color: ${getColor("text.main")} !important;
            `,
        },
        icon: {
            iconProps: {
                src: Delete,
                color: "primary.main",
                size: 16,
            },
            position: "left",
        },
        css: css`
            &:hover > span,
            &:focus > span {
                color: ${getColor("primary")} !important;
            }

            &.hide-on-small {
                @media (max-width: 800px) {
                    display: none;
                }
            }
        `,
    },
    saveOrderClickable: {
        saveLink: {
            css: css`
                @media (min-width: 801px) {
                    display: none;
                }
            `,
        },
    },
    saveOrderClickableHidden: {
        saveLink: {
            css: css`
                @media (max-width: 800px) {
                    display: none;
                }
            `,
        },
    },
    addAllToListClickable: {
        typographyProps: {
            css: css`
                color: ${getColor("text.main")} !important;
            `,
        },
        icon: {
            iconProps: {
                src: AddOutline,
                color: "primary.main",
                size: 13,
            },
            position: "left",
        },
        css: css`
            &:hover > span,
            &:focus > span {
                color: ${getColor("primary")} !important;
            }
            &.hide-on-small {
                @media (max-width: 800px) {
                    display: none;
                }
            }
        `,
    },
    checkboxesWrapper: {
        css: css`
            max-width: 362px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;

            @media (max-width: 800px) {
                max-width: 100%;
                flex-direction: row;
                justify-content: space-between;
                margin-top: 15px;
                flex-wrap: wrap;
                width: 100%;
            }
        `,
    },
    certificationCheckboxesFirst: {
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
            css: css`
                margin-left: 5px !important;
            `,
        },
    },
    certificationCheckboxes: {
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
            css: css`
                margin-left: 5px !important;
            `,
        },
    },
};

const styles = cartActionsStyles;

const CartActions: FC<Props> = ({
    cart,
    isCartEmpty,
    canAddAllToList,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    SetAllCartLinesCertificate,
    isPunchOutOrder,
    onRemoveAllClick,
    customSettings,
}) => {
    if (!cart || isPunchOutOrder || isCartEmpty) {
        return null;
    }

    const toasterContext = React.useContext(ToasterContext);
    const [allProductsEis, setAllProductsEis] = React.useState(false);
    const [allProductsMfr, setAllProductsMfr] = React.useState(false);
    const [isOpenModal, setIsOpenModal] = React.useState(false);

    const enableAuthenticatedExperience = customSettings.enableAuthenticatedExperience.toLowerCase() === "true";

    const addAllToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfos =
            cart.cartLines
                ?.filter(o => o.productId && o.qtyOrdered)
                .map(cartLine => ({
                    productId: cartLine.productId!,
                    qtyOrdered: cartLine.qtyOrdered!,
                    unitOfMeasure: cartLine.unitOfMeasure,
                })) || [];
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    React.useEffect(() => {
        if (cart && cart.cartLines?.length) {
            const setAllEIS = cart.cartLines.every(line => line.properties["eis/GCGCertification"] === "true");
            const setAllMFR = cart.cartLines.every(line => line.properties["mfrCertification"] === "true");
            setAllProductsEis(setAllEIS);
            setAllProductsMfr(setAllMFR);
        }
    }, [cart]);

    const onAllProductsEisHanlder = (event: React.SyntheticEvent, value: boolean) => {
        setAllProductsEis(value);
        SetAllCartLinesCertificate({ eisCertificate: value  } as any);
    };

    const onAllProductsMfrHanlder = (event: React.SyntheticEvent, value: boolean) => {
        setAllProductsMfr(value);
        SetAllCartLinesCertificate({ mfrCertificate: value } as any);
    };

    const closeConfirmationModalHandler = () => {
        setIsOpenModal(false);
    };

    return (
        <StyledWrapper {...styles.narrowHidden} data-test-selector="cartActionsNarrow">
            <Link
                onClick={() => setIsOpenModal(true)}
                {...styles.removeAllLink}
                className="hide-on-small"
                data-test-selector="cartlineHeader_removeAll"
            >
                {translate("Remove All Products")}
            </Link>
            <Modal
                headline={translate("Confirm Deletion")}
                isOpen={isOpenModal}
                handleClose={closeConfirmationModalHandler}
            >
                <StyledWrapper>
                    {translate("Are you sure you want to remove all items from your shopping cart?")}
                </StyledWrapper>
                <StyledWrapper className="modal-actions">
                    <Button variant="secondary" onClick={closeConfirmationModalHandler}>
                        {translate("Cancel")}
                    </Button>
                    <Button variant="primary" onClick={onRemoveAllClick}>
                        {translate("Remove All")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <CartSaveOrderButton variant="link" extendedStyles={styles.saveOrderClickableHidden} />

            {enableAuthenticatedExperience && (
                <Link
                    {...styles.addAllToListClickable}
                    key="addAll"
                    disabled={!canAddAllToList}
                    className="hide-on-small"
                    onClick={addAllToListClickHandler}
                    data-test-selector="cartActionsAddAllToList"
                >
                    {translate("Add All To Shopping List")}
                </Link>
            )}

            <Hidden {...styles.hiddenOnLarge}>
                <OverflowMenu {...styles.buttonsOverflowPopup}>
                    <Link
                        onClick={() => setIsOpenModal(true)}
                        {...styles.removeAllLink}
                        data-test-selector="cartlineHeader_removeAll"
                    >
                        {translate("Remove All Products")}
                    </Link>
                    <Modal
                        headline={translate("Confirm Deletion")}
                        isOpen={isOpenModal}
                        handleClose={closeConfirmationModalHandler}
                    >
                        <StyledWrapper>
                            {translate("Are you sure you want to remove all items from your shopping cart?")}
                        </StyledWrapper>
                        <StyledWrapper className="modal-actions">
                            <Button variant="secondary" onClick={closeConfirmationModalHandler}>
                                {translate("Cancel")}
                            </Button>
                            <Button variant="primary" onClick={onRemoveAllClick}>
                                {translate("Remove All")}
                            </Button>
                        </StyledWrapper>
                    </Modal>
                    <CartSaveOrderButton variant="link" extendedStyles={styles.saveOrderClickable} />

                    {enableAuthenticatedExperience && (
                        <Link
                            {...styles.addAllToListClickable}
                            key="addAll"
                            disabled={!canAddAllToList}
                            onClick={addAllToListClickHandler}
                            data-test-selector="cartActionsAddAllToList"
                        >
                            {translate("Add All To Shopping List")}
                        </Link>
                    )}
                </OverflowMenu>
            </Hidden>
            <StyledWrapper>
                {
                    cart?.properties?.isPunchout === "true"
                        ?
                        <></>
                        :
                        <StyledWrapper {...styles.checkboxesWrapper}>
                            <StyledWrapper
                                css={css`
                        margin-bottom: 5px;
                        line-height: 18px;
                        display: flex;
                        align-items: center;
                        @media (max-width: 800px) {
                            margin: 0;
                        }
                    `}
                            >
                                <Checkbox
                                    checked={allProductsEis}
                                    data-test-selector="all_products_eis_certificate"
                                    onChange={onAllProductsEisHanlder}
                                    {...styles.certificationCheckboxesFirst}
                                >
                                    {siteMessage("Custom_site_request_certification_for_all")}
                                </Checkbox>
                                <XPopup
                                    type="tooltip"
                                    trigger={
                                        <StyledWrapper>
                                            <Icon
                                                src={Info}
                                                size={16}
                                                css={css`
                                        margin-left: 5px;
                                    `}
                                                color="secondary"
                                            />
                                        </StyledWrapper>
                                    }
                                    position="top"
                                    fixed
                                >
                                    <StyledWrapper>{siteMessage("Custom_site_request_certificate_tooltip_msg")}</StyledWrapper>
                                </XPopup>
                            </StyledWrapper>
                            <StyledWrapper
                                css={css`
                        display: flex;
                        align-items: center;
                    `}
                            >
                                <Checkbox
                                    checked={allProductsMfr}
                                    data-test-selector="all_products_mfr_certificate"
                                    onChange={onAllProductsMfrHanlder}
                                    {...styles.certificationCheckboxes}
                                >
                                    {translate("Request Mfr. Certification For All Products")}
                                </Checkbox>
                                <XPopup
                                    type="tooltip"
                                    trigger={
                                        <StyledWrapper>
                                            <Icon
                                                src={Info}
                                                size={16}
                                                css={css`
                                        margin-left: 5px;
                                    `}
                                                color="secondary"
                                            />
                                        </StyledWrapper>
                                    }
                                    position="top"
                                    fixed
                                >
                                    <StyledWrapper>{siteMessage("Custom_site_request_mfr_certificate_tooltip_msg")}</StyledWrapper>
                                </XPopup>
                            </StyledWrapper>
                        </StyledWrapper>
                }
            </StyledWrapper>
        </StyledWrapper>
    );
};

// omitting these props from the widget in order to use it as a component
type CartActionsButtonsProp = Omit<
    typeof CartActions,
    "id" | "type" | "zone" | "parentId" | "generalFields" | "translatableFields" | "contextualFields" | "fields"
>;

// using this widget as a component
export const CartActionButtons: CartActionsButtonsProp & any = connect(
    mapStateToProps,
    mapDispatchToProps,
)(CartActions);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartActions),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
    },
};

export default widgetModule;
